/**
 * Staff event create/edit form (FEA-23, docs/FRONTEND-PLAN.md section 2.5). Shared
 * by Events.jsx ("New event") and EventDetail.jsx ("Edit event") so both go
 * through the same allowlist shape - `POST /events` and `PUT /events/{id}`
 * accept the identical body: {name, description, venue, startsAt, endsAt,
 * capacity, status, accreditedOnly, metadata}; unknown keys 400.
 *
 * Rendered inside a `Modal` by the caller (mirrors ScheduleForm/Modal
 * composition in Schedules.jsx) rather than owning the dialog itself.
 */
import { useState } from 'react';
import { EVENT_STATUSES } from '../api/events';
import { ErrorAlert, Field, SelectField, TextareaField } from './ui';
import { cn, ui } from '../lib/ui';

/** `<input type="datetime-local">` wants a local, zone-less string. */
function pad2(n) {
  return String(n).padStart(2, '0');
}

function toLocalInput(iso) {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return (
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` +
    `T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
  );
}

export function emptyEventForm() {
  return {
    name: '',
    description: '',
    venue: '',
    format: 'in-person',
    startsAt: '',
    endsAt: '',
    capacity: '',
    status: 'draft',
    accreditedOnly: false,
    metadata: ''
  };
}

export function eventToForm(event) {
  return {
    name: event.name || '',
    description: event.description || '',
    venue: event.venue || '',
    format: event.format || 'in-person',
    startsAt: toLocalInput(event.startsAt),
    endsAt: toLocalInput(event.endsAt),
    capacity: event.capacity == null ? '' : String(event.capacity),
    status: event.status || 'draft',
    accreditedOnly: event.accreditedOnly === true,
    metadata: event.metadata && Object.keys(event.metadata).length
      ? JSON.stringify(event.metadata, null, 2)
      : ''
  };
}

/**
 * Build the `POST/PUT /events` body from form state. Throws with a plain
 * message if the metadata textarea holds invalid JSON - the caller should
 * surface that as a form error rather than sending it.
 */
export function buildEventPayload(form) {
  const trimmedMetadata = form.metadata.trim();
  let metadata;
  if (trimmedMetadata) {
    try {
      metadata = JSON.parse(trimmedMetadata);
    } catch {
      throw new Error('Metadata must be valid JSON (or left blank).');
    }
  }

  const payload = {
    name: form.name.trim(),
    description: form.description.trim() || null,
    venue: form.venue.trim() || null,
    format: form.format || 'in-person',
    startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
    endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
    capacity: form.capacity === '' ? null : Number(form.capacity),
    status: form.status,
    accreditedOnly: form.accreditedOnly === true
  };
  if (metadata !== undefined) payload.metadata = metadata;
  return payload;
}

export default function EventForm({ initial, submitLabel, onSubmit, onCancel }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const payload = buildEventPayload(form);
      await onSubmit(payload);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <ErrorAlert error={error} />
      <Field
        label="Name"
        required
        value={form.name}
        onChange={(e) => set({ name: e.target.value })}
      />
      <TextareaField
        label="Description (optional)"
        rows={3}
        value={form.description}
        onChange={(e) => set({ description: e.target.value })}
      />
      <SelectField
        label="Format"
        hint="Virtual and hybrid events can confirm attendance from an attendee list after the event starts."
        value={form.format}
        onChange={(e) => set({ format: e.target.value })}
      >
        <option value="in-person">In person</option>
        <option value="virtual">Virtual</option>
        <option value="hybrid">Hybrid (in person and online)</option>
      </SelectField>
      <Field
        label={form.format === 'virtual' ? 'Meeting details (optional)' : 'Venue (optional)'}
        value={form.venue}
        onChange={(e) => set({ venue: e.target.value })}
      />
      <Field
        label="Starts"
        type="datetime-local"
        required
        step="900"
        value={form.startsAt}
        onChange={(e) => {
          const next = { startsAt: e.target.value };
          if (!form.endsAt || form.endsAt <= e.target.value) {
            const s = new Date(e.target.value);
            if (!Number.isNaN(s.getTime())) {
              const pad = (n) => String(n).padStart(2, '0');
              const end = new Date(s.getTime() + 60 * 60 * 1000);
              next.endsAt =
                `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}` +
                `T${pad(end.getHours())}:${pad(end.getMinutes())}`;
            }
          }
          set(next);
        }}
      />
      <Field
        label="Ends (optional)"
        type="datetime-local"
        step="900"
        value={form.endsAt}
        onChange={(e) => set({ endsAt: e.target.value })}
      />
      <Field
        label="Capacity (optional)"
        type="number"
        min="1"
        hint="Leave blank for unlimited capacity."
        value={form.capacity}
        onChange={(e) => set({ capacity: e.target.value })}
      />
      <SelectField
        label="Status"
        hint="Publishing makes the event visible to members; cancelling closes registration."
        value={form.status}
        onChange={(e) => set({ status: e.target.value })}
      >
        {EVENT_STATUSES.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </SelectField>
      <label className={cn(ui.row, 'text-body-s')}>
        <input
          type="checkbox"
          checked={form.accreditedOnly}
          onChange={(e) => set({ accreditedOnly: e.target.checked })}
        />{' '}
        Restrict registration to verified accredited investors
      </label>
      <TextareaField
        label="Metadata (optional JSON)"
        hint="Advanced — arbitrary key/value data stored with the event. Leave blank to omit."
        rows={2}
        value={form.metadata}
        onChange={(e) => set({ metadata: e.target.value })}
      />
      <div className={cn(ui.row, 'mt-4')}>
        <button type="submit" className={ui.btnPrimary} disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className={ui.btnSecondary} disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
